/**
 * Activity / Submarine Sonar - Phase 3 wiring.
 *
 * Live data sources:
 *   GET /v1/activity/sonar          → per-application acoustic signatures
 *   GET /v1/activity/sonar/findings → active drift alerts
 *
 * Sonar's job is to detect when an application's query mix changes
 * (KL-divergence vs its rolling baseline). The page reads both the
 * signature catalogue (so the radar charts have something to render)
 * and the open findings (so the "Active sonar alerts" panel reflects
 * real drift).
 *
 * Until enough activity events accumulate to compute a baseline,
 * Sonar's tables are empty - the page shows a clean "no signatures
 * computed yet" state rather than synthetic data.
 */

import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Waves, AlertTriangle, Loader2, ArrowUpRight, Sparkles, CheckCircle2, ShieldX, ShieldCheck } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar as RRadar } from 'recharts';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import {
  listSonarSignatures,
  listSonarFindings, analyzeSonar, acknowledgeSonarFinding,
  patchSonarFindingStatus,
  type SonarSignature,
  type SonarFinding,
} from '@/lib/api';
import {
  FindingStatusDialog,
  type FindingTransition,
} from '@/components/activity/FindingStatusDialog';

/**
 * The signature_distribution JSONB on the server is keyed by feature
 * bucket; we project it onto the 8 axes the radar chart needs. Missing
 * buckets default to 0 so the polygon still has a shape.
 */
const FEATURE_AXES: Array<{ key: string; label: string }> = [
  { key: 'statementType.SELECT',  label: 'SELECT' },
  { key: 'statementType.INSERT',  label: 'INSERT' },
  { key: 'statementType.UPDATE',  label: 'UPDATE' },
  { key: 'statementType.DELETE',  label: 'DELETE' },
  { key: 'statementType.EXPORT',  label: 'EXPORT' },
  { key: 'tables.pii',            label: 'PII tables' },
  { key: 'hours.0-6',             label: 'Hours 0-6' },
  { key: 'hours.9-18',            label: 'Hours 9-18' },
];

function project(dist: SonarSignature['signatureDistribution'] | undefined, axisKey: string): number {
  if (!dist) return 0;
  const [bucket, key] = axisKey.split('.');
  const inner = dist?.[bucket!];
  const v = inner?.[key!];
  return typeof v === 'number' ? v : 0;
}

export function ActivitySonar() {
  const [signatures, setSignatures] = useState<SonarSignature[]>([]);
  const [findings, setFindings] = useState<SonarFinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeStatus, setAnalyzeStatus] = useState<string | null>(null);
  const [ackingId, setAckingId] = useState<string | null>(null);
  const [ackedIds, setAckedIds] = useState<Set<string>>(new Set());

  // Fix D UI (2026-05-28) - dismiss/escalate dialog state. See UEBA.tsx
  // for the rationale; same pattern, parallel page.
  const [dialogState, setDialogState] = useState<
    | { open: true; id: string; status: FindingTransition; context: string }
    | { open: false }
  >({ open: false });
  const [transitionedIds, setTransitionedIds] = useState<Set<string>>(new Set());

  function openTransitionDialog(f: SonarFinding, status: FindingTransition): void {
    setDialogState({
      open: true,
      id: f.id,
      status,
      context: `${f.applicationName} · ${f.pattern}`,
    });
  }

  async function handleTransition(reason: string): Promise<void> {
    if (!dialogState.open) return;
    const { id, status } = dialogState;
    await patchSonarFindingStatus(id, status, reason);
    setTransitionedIds((prev) => new Set([...prev, id]));
  }

  async function handleAckSonar(id: string): Promise<void> {
    setAckingId(id);
    try {
      await acknowledgeSonarFinding(id);
      setAckedIds((prev) => new Set([...prev, id]));
    } catch {
      setAnalyzeStatus('Acknowledge failed - refresh and try again.');
    } finally {
      setAckingId(null);
    }
  }

  // Wave 2 (2026-05-27) - surface POST /v1/activity/sonar/analyze.
  // Computes per-app KL-divergence between the last 24h signature
  // and the 14d baseline; persists any fired findings.
  async function handleAnalyze(): Promise<void> {
    setAnalyzing(true);
    setAnalyzeStatus(null);
    try {
      const result = await analyzeSonar({ recentHours: 24, baselineDays: 14 });
      const n = Array.isArray(result.findings) ? result.findings.length : 0;
      setAnalyzeStatus(
        n === 0
          ? 'Analysis complete - no acoustic drift detected in the last 24h.'
          : `Analysis complete - ${n} signature${n === 1 ? '' : 's'} drifted vs baseline.`,
      );
      await refresh();
    } catch (e) {
      setAnalyzeStatus(e instanceof Error ? `Analysis failed: ${e.message}` : 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  }

  const refresh = (signal?: AbortSignal) =>
    Promise.allSettled([
      listSonarSignatures(),
      listSonarFindings({ limit: 50 }),
    ]).then(([sigRes, findRes]) => {
      if (signal?.aborted) return;
      const sigs = sigRes.status === 'fulfilled' ? sigRes.value.signatures : [];
      const fnds = findRes.status === 'fulfilled' ? findRes.value.findings : [];
      const errs: string[] = [];
      if (sigRes.status === 'rejected') errs.push(`signatures: ${(sigRes.reason as Error)?.message ?? 'failed'}`);
      if (findRes.status === 'rejected') errs.push(`findings: ${(findRes.reason as Error)?.message ?? 'failed'}`);
      setSignatures(sigs);
      setFindings(fnds);
      setError(errs.length > 0 ? errs.join(' · ') : null);
      setLoading(false);
    });

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  const openFindings = findings.filter((f) => f.status === 'open');

  return (
    <PageContainer>
      <PageHeader
        title="Submarine Sonar"
        subtitle="Per-application acoustic signatures (statementType + table + hour distributions). KL-divergence detects when an app starts behaving like a different app."
        badges={[
          { label: 'KL-Divergence', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Degraded', variant: 'warning' as const }]
              : [{ label: `${signatures.length} apps`, variant: 'success' as const }]),
        ]}
        actions={
          <Button
            size="sm"
            onClick={() => void handleAnalyze()}
            disabled={analyzing}
            title="Compute KL-divergence for the last 24h vs the 14d baseline"
          >
            <Sparkles className={`h-3.5 w-3.5 mr-1.5 ${analyzing ? 'animate-pulse' : ''}`} />
            {analyzing ? 'Analyzing…' : 'Analyze now'}
          </Button>
        }
      />
      {analyzeStatus && (
        <div className="glass-lite rounded-md p-3 mb-3 text-xs text-muted-foreground">
          {analyzeStatus}
        </div>
      )}

      {loading && <LoadingState caption="Loading sonar signatures + findings…" />}
      {error && !loading && (
        <ErrorState
          title="Some sonar data unavailable"
          detail={error}
          onRetry={() => { setError(null); setLoading(true); refresh(); }}
        />
      )}

      {!loading && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <GlassCard className="lg:col-span-2 p-6">
              <CardHeader className="p-0 mb-4">
                <CardTitle>KL-divergence by application</CardTitle>
                <CardDescription>Each bar is the most recent KL-divergence vs the app's rolling baseline. Spike = app behaviour changed.</CardDescription>
              </CardHeader>
              {signatures.length === 0 ? (
                <div className="text-xs text-muted-foreground py-10 text-center">
                  No application signatures computed yet. The scheduler runs Sonar on a fixed cadence as activity events accumulate.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart
                    data={signatures.map((s) => ({
                      name: s.applicationName,
                      kl: s.lastKlDivergence ? parseFloat(s.lastKlDivergence) : 0,
                    }))}
                  >
                    <defs>
                      <linearGradient id="kl-grad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--chart-2))" stopOpacity={0.55} />
                        <stop offset="100%" stopColor="hsl(var(--chart-2))" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                    <XAxis dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} stroke="hsl(var(--border))" />
                    <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} stroke="hsl(var(--border))" />
                    <RTooltip contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                    <Area type="monotone" dataKey="kl" stroke="hsl(var(--chart-2))" fill="url(#kl-grad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </GlassCard>

            <GlassCard className="p-6">
              <CardHeader className="p-0 mb-3">
                <CardTitle>Active sonar alerts</CardTitle>
                <CardDescription>Open findings emitted by the Sonar engine</CardDescription>
              </CardHeader>
              {openFindings.length === 0 ? (
                <div className="text-xs text-muted-foreground py-4 text-center">No open Sonar findings.</div>
              ) : (
                <div className="space-y-2">
                  {openFindings.slice(0, 8).map((a, i) => (
                    <motion.div
                      key={a.id}
                      initial={{ opacity: 0, scale: 0.97 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.05 }}
                      className="glass-lite p-3 rounded-md"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Waves className="h-3.5 w-3.5 text-primary" />
                        <code className="font-mono text-xs truncate">{a.applicationName}</code>
                        <Badge variant={a.severity} className="text-[9px] ml-auto capitalize">{a.severity}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{a.pattern}</p>
                      {a.rationale && <p className="text-[10px] text-muted-foreground/80 mt-1">{a.rationale}</p>}
                      {/* P1-5 (2026-05-26) - jump to Decisions filtered
                          by the application name. */}
                      <div className="mt-1.5 flex items-center justify-end gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant={ackedIds.has(a.id) ? 'glass' : 'default'}
                          onClick={() => void handleAckSonar(a.id)}
                          disabled={ackedIds.has(a.id) || ackingId === a.id || transitionedIds.has(a.id)}
                          title="Claim ownership of this finding"
                          className="h-6 text-[10px] px-2"
                        >
                          {ackingId === a.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <CheckCircle2 className="h-3 w-3" />
                          )}
                          {ackedIds.has(a.id) ? 'Acked' : 'Ack'}
                        </Button>
                        {/* Fix D UI (2026-05-28) - dismiss/escalate.
                            Status transition is captured into the
                            audit chain with the operator's reason. */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openTransitionDialog(a, 'dismissed')}
                          disabled={transitionedIds.has(a.id)}
                          title="Dismiss as false-positive"
                          className="h-6 text-[10px] px-2"
                        >
                          <ShieldX className="h-3 w-3" />
                          Dismiss
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => openTransitionDialog(a, 'escalated')}
                          disabled={transitionedIds.has(a.id)}
                          title="Escalate to incident-response"
                          className="h-6 text-[10px] px-2"
                        >
                          <ShieldCheck className="h-3 w-3" />
                          Escalate
                        </Button>
                        <Link
                          to="/detection/decisions"
                          search={{ q: a.applicationName } as never}
                          className="text-[11px] text-primary hover:underline inline-flex items-center gap-0.5"
                          title={`See decisions involving ${a.applicationName}`}
                        >
                          Decisions <ArrowUpRight className="h-3 w-3" />
                        </Link>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </GlassCard>
          </div>

          <GlassCard className="p-6">
            <CardHeader className="p-0 mb-3">
              <CardTitle>Application acoustic signature</CardTitle>
              <CardDescription>
                Each axis projects one feature bucket from <code>signature_distribution</code>; the polygon is the app's behavioral fingerprint.
              </CardDescription>
            </CardHeader>
            {signatures.length === 0 ? (
              <div className="text-xs text-muted-foreground py-6 text-center">
                Radar charts appear once signatures have been computed for at least one application.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {signatures.slice(0, 6).map((s) => {
                  const data = FEATURE_AXES.map((ax) => ({
                    feature: ax.label,
                    current: project(s.signatureDistribution, ax.key),
                  }));
                  const kl = s.lastKlDivergence ? parseFloat(s.lastKlDivergence) : 0;
                  const drift = kl > 0.3;
                  return (
                    <div key={s.id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <code className="text-xs font-mono truncate">{s.applicationName}</code>
                        <Badge variant={drift ? 'critical' : 'success'} className="text-[9px]">
                          {drift ? `Drift ${kl.toFixed(2)} KL` : 'Within baseline'}
                        </Badge>
                      </div>
                      <ResponsiveContainer width="100%" height={200}>
                        <RadarChart data={data}>
                          <PolarGrid stroke="hsl(var(--border))" />
                          <PolarAngleAxis dataKey="feature" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 9 }} />
                          <PolarRadiusAxis tick={false} axisLine={false} />
                          <RRadar
                            name="Current"
                            dataKey="current"
                            stroke={drift ? 'hsl(var(--sev-critical))' : 'hsl(var(--chart-1))'}
                            fill={drift ? 'hsl(var(--sev-critical))' : 'hsl(var(--chart-1))'}
                            fillOpacity={0.3}
                            strokeWidth={2}
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })}
              </div>
            )}
          </GlassCard>
        </>
      )}

      {/* Fix D UI (2026-05-28) - always-rendered dialog (controlled via
          `open`). See UEBA.tsx for the rationale - mount/unmount race
          breaks Radix's portal/content layering. */}
      <FindingStatusDialog
        open={dialogState.open}
        status={dialogState.open ? dialogState.status : 'dismissed'}
        context={dialogState.open ? dialogState.context : ''}
        onSubmit={handleTransition}
        onClose={() => setDialogState({ open: false })}
      />
    </PageContainer>
  );
}
